import {
  fetchTenantConfig,
  FetchTenantConfigOptions,
  TenantConfig,
  TenantConfigResult,
} from './tenant-config';

/**
 * WI-F4 — Bootstrap decision logic (ADO #38532).
 *
 * Pure, side-effect-free helpers the boot sequence (`main.tsx`) uses to decide
 * what to render from a {@link TenantConfigResult}, plus a bounded-timeout
 * wrapper around {@link fetchTenantConfig}. Kept in `libs/core` (not the app)
 * so the decision logic is unit-testable without a DOM / React root, and so the
 * app depends DOWN on libs, never the reverse.
 */

/**
 * What the boot sequence should render. `variant` is structurally identical to
 * the app's `TenantErrorVariant` (kept as a string union here so `libs/core`
 * does not import UP into `apps/web-client`).
 */
export type BootDecision =
  | { kind: 'ready'; config: TenantConfig }
  | {
      kind: 'tenant-error';
      variant: 'tenant-not-found' | 'config-unavailable';
    };

/**
 * Map a {@link TenantConfigResult} to a {@link BootDecision}.
 *
 * - `success`            → `ready` (construct MSAL, render app).
 * - `tenant-not-found`   → tenant-error screen, `tenant-not-found` variant.
 * - `config-unavailable` → tenant-error screen, `config-unavailable` variant
 *                          (also the safe default for any unexpected status).
 *
 * This is DISTINCT from an MSAL-init failure, which the caller surfaces
 * separately (a config-fetch problem is not an auth problem).
 */
export const decideBoot = (result: TenantConfigResult): BootDecision => {
  switch (result.status) {
    case 'success':
      return { kind: 'ready', config: result.config };
    case 'tenant-not-found':
      return { kind: 'tenant-error', variant: 'tenant-not-found' };
    case 'config-unavailable':
    default:
      return { kind: 'tenant-error', variant: 'config-unavailable' };
  }
};

/** Default boot-safety ceiling for the config fetch. */
export const CONFIG_FETCH_TIMEOUT_MS = 15000;

/**
 * Fetch the tenant config with a bounded timeout so a hung request surfaces as
 * `config-unavailable` (→ error screen) instead of stalling the loader forever.
 *
 * Uses `Promise.race`: whichever of the fetch or the timeout settles first
 * wins. On timeout we `abort()` the request via an `AbortController` so the
 * underlying HTTP call is actually cancelled — not just abandoned while it keeps
 * running in the background. The timer is cleared when the fetch settles so it
 * cannot leak.
 */
export const fetchTenantConfigWithTimeout = (
  options?: FetchTenantConfigOptions,
  timeoutMs: number = CONFIG_FETCH_TIMEOUT_MS
): Promise<TenantConfigResult> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<TenantConfigResult>((resolve) => {
    timer = setTimeout(() => {
      // Actually abort the in-flight request, then resolve the race.
      controller.abort();
      resolve({
        status: 'config-unavailable',
        reason: `Tenant config fetch timed out after ${timeoutMs}ms.`,
      });
    }, timeoutMs);
  });

  return Promise.race([
    fetchTenantConfig({ ...options, signal: controller.signal }).finally(() =>
      clearTimeout(timer)
    ),
    timeout,
  ]);
};
